import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { authAPI } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { Eye, EyeOff, Loader2, Heart } from 'lucide-react';
import { BRAND_ASSETS } from '../config/brandAssets';

const LOGO_URL = BRAND_ASSETS.LOGO_WORDMARK;
const LOGO_SMALL = BRAND_ASSETS.LOGO_PRIMARY;

const Login = () => {
    const [username, setUsername] = useState('');
    const [password, setPassword] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [loading, setLoading] = useState(false);
    const [initializing, setInitializing] = useState(true);
    const { login, user } = useAuth();
    const navigate = useNavigate();

    useEffect(() => {
        if (user) {
            navigate('/dashboard');
        }
    }, [user, navigate]);

    useEffect(() => {
        // Initialize admin on first load
        const initAdmin = async () => {
            try {
                await authAPI.initAdmin();
            } catch (error) {
                // Admin might already exist
            } finally {
                setInitializing(false);
            }
        };
        initAdmin();
    }, []);

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!username || !password) {
            toast.error('Please enter username and password');
            return;
        }

        setLoading(true);
        try {
            await login(username, password);
            toast.success('Login successful!');
            navigate('/dashboard');
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Login failed');
        } finally {
            setLoading(false);
        }
    };

    if (initializing) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-[#1A1A1A]">
                <Loader2 className="w-8 h-8 animate-spin text-[#C08478]" />
            </div>
        );
    }

    return (
        <div className="login-container" style={{ fontFamily: "'Century Gothic','CenturyGothic','Manrope','DM Sans',Arial,sans-serif" }}>
            {/* Hero Section - Logo with text at bottom left */}
            <div className="login-hero hidden lg:flex flex-col bg-[#000000] relative">
                {/* Empty space for centering effect */}
                <div className="flex-1"></div>
                
                {/* Logo and text at bottom left */}
                <div className="p-12 pb-16">
                    <img 
                        src={LOGO_URL} 
                        alt="UNIVERS COSMETIC" 
                        className="w-72 h-auto object-contain mb-6"
                    />
                    <p className="text-lg text-white/70">
                        Manage your daily sales with precision.
                    </p>
                    <p className="text-lg text-white/70">
                        Track payments, monitor dues, and generate comprehensive reports.
                    </p>
                </div>
                
                {/* Decorative line */}
                <div className="absolute bottom-0 left-0 right-0 h-1 bg-gradient-to-r from-transparent via-[#C08478] to-transparent"></div>
            </div>

            {/* Login Form */}
            <div className="login-form bg-white">
                <div className="w-full max-w-sm mx-auto">
                    {/* Logo for mobile */}
                    <div className="lg:hidden mb-8 text-center">
                        <img 
                            src={LOGO_SMALL} 
                            alt="UNIVERS COSMETIC" 
                            className="w-24 h-24 object-contain mx-auto mb-4"
                        />
                    </div>

                    <div className="hidden lg:block mb-8">
                        <img 
                            src={LOGO_SMALL} 
                            alt="UNIVERS COSMETIC" 
                            className="w-16 h-16 object-contain mb-6"
                        />
                        <h1 className="text-3xl font-bold text-[#1A1A1A] mb-2">WELCOME MATE</h1>
                        <p className="text-gray-500">SIGN IN TO ACCESS YOUR SALES DASHBOARD.</p>
                    </div>

                    <form onSubmit={handleSubmit} className="space-y-6">
                        <div className="space-y-2">
                            <Label htmlFor="username" className="text-sm font-medium text-gray-700">
                                Username
                            </Label>
                            <Input
                                id="username"
                                data-testid="login-username"
                                type="text"
                                value={username}
                                onChange={(e) => setUsername(e.target.value)}
                                placeholder="Enter your username"
                                className="h-12 bg-[#F5F5F5] border-[#E5E5E5] focus:border-[#C08478] focus:ring-[#C08478]/20"
                            />
                        </div>

                        <div className="space-y-2">
                            <Label htmlFor="password" className="text-sm font-medium text-gray-700">
                                Password
                            </Label>
                            <div className="relative">
                                <Input
                                    id="password"
                                    data-testid="login-password"
                                    type={showPassword ? 'text' : 'password'}
                                    value={password}
                                    onChange={(e) => setPassword(e.target.value)}
                                    placeholder="Enter your password"
                                    className="h-12 bg-[#F5F5F5] border-[#E5E5E5] focus:border-[#C08478] focus:ring-[#C08478]/20 pr-10"
                                />
                                <button
                                    type="button"
                                    onClick={() => setShowPassword(!showPassword)}
                                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                                >
                                    {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                                </button>
                            </div>
                        </div>

                        <Button
                            type="submit"
                            data-testid="login-submit"
                            disabled={loading}
                            className="w-full h-12 bg-[#1A1A1A] hover:bg-[#333] text-white rounded-full font-medium transition-all duration-300 hover:shadow-lg hover:-translate-y-0.5"
                        >
                            {loading ? (
                                <>
                                    <Loader2 className="w-4 h-4 animate-spin mr-2" />
                                    Signing in...
                                </>
                            ) : (
                                'Sign In'
                            )}
                        </Button>
                    </form>

                    <div className="mt-8 p-4 bg-[#F5F5F5] rounded-xl">
                        <p className="text-xs text-gray-500 text-center flex items-center justify-center gap-1">
                            Created with <Heart size={14} className="text-red-500 fill-red-500" /> By <span className="font-semibold text-[#C08478]">#SAMSAYHIN</span>
                        </p>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default Login;
